import { css } from 'astroturf';
import React from 'react';
import { ArrowRight } from 'react-bytesize-icons';
import { CheatsheetCard } from './cheatsheet-card';
import { Section, PropertyRow, CodeBlock } from './cheatsheet-elements';

const styles = css`
  .twoColumnGrid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.25rem;
  }

  .positionAreaGrid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.125rem;
    background-color: var(--sheet-slate-900-50);
    border-radius: 0.25rem;
    padding: 0.375rem;
    border-width: 1px;
    border-style: solid;
    border-color: var(--sheet-slate-600-30);
  }

  .positionAreaCellMuted {
    height: 1.25rem;
    background-color: var(--sheet-slate-700-50);
    border-radius: 0.125rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 9px;
    line-height: 1.2;
    color: var(--sheet-slate-400);
  }

  .positionAreaCellHighlight {
    height: 1.25rem;
    background-color: var(--sheet-slate-600-50);
    border-radius: 0.125rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 9px;
    line-height: 1.2;
    color: var(--sheet-emerald-400);
  }

  .positionAreaAnchorCell {
    height: 1.25rem;
    background-color: var(--sheet-cyan-500-30);
    border-radius: 0.125rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 9px;
    line-height: 1.2;
    color: var(--sheet-cyan-300);
  }

  .fallbackFlowRow {
    display: flex;
    gap: 0.375rem;
    margin-bottom: 2rem;
    margin-top: 2rem;
  }

  .fallbackCase {
    position: relative;
    background-color: var(--sheet-slate-900-50);
    border-radius: 0.25rem;
    padding: 0.5rem;
    border-width: 1px;
    border-style: solid;
    border-color: var(--sheet-slate-600-30);
  }

  .fallbackButton {
    width: 2.5rem;
    height: 1.25rem;
    background-color: var(--sheet-cyan-500-70);
    border-radius: 0.25rem;
    font-size: 9px;
    line-height: 1.2;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--sheet-slate-900);
    font-weight: 500;
  }

  .fallbackTipBottom {
    position: absolute;
    bottom: -0.875rem;
    left: 50%;
    transform: translateX(-50%);
    width: 2rem;
    height: 0.875rem;
    background-color: var(--sheet-emerald-500);
    border-radius: 0.25rem;
    font-size: 9px;
    line-height: 1.2;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--sheet-slate-900);
    font-weight: 500;
  }

  .fallbackLabelTop {
    font-size: 10px;
    line-height: 1.2;
    color: var(--sheet-slate-500);
    position: absolute;
    top: -1rem;
    left: 50%;
    transform: translateX(-50%);
  }

  .fallbackArrowWrap {
    color: var(--sheet-slate-500);
    align-self: flex-start;
  }

  .fallbackArrow {
    height: 0.875rem;
    width: 0.875rem;
    color: var(--sheet-slate-500);
  }

  .fallbackTipTop {
    position: absolute;
    top: -0.875rem;
    left: 50%;
    transform: translateX(-50%);
    width: 2rem;
    height: 0.875rem;
    background-color: var(--sheet-emerald-500);
    border-radius: 0.25rem;
    font-size: 9px;
    line-height: 1.2;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--sheet-slate-900);
    font-weight: 500;
  }

  .fallbackLabelBottom {
    font-size: 10px;
    line-height: 1.2;
    color: var(--sheet-slate-500);
    position: absolute;
    bottom: -1rem;
    left: 50%;
    transform: translateX(-50%);
    white-space: nowrap;
  }

  .fallbackTipInline {
    position: absolute;
    top: 50%;
    left: -1.75rem;
    transform: translateY(-50%);
    width: 2rem;
    height: 0.875rem;
    background-color: var(--sheet-emerald-500);
    border-radius: 0.25rem;
    font-size: 9px;
    line-height: 1.2;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--sheet-slate-900);
    font-weight: 500;
  }

  .fallbackLabelInline {
    font-size: 10px;
    line-height: 1.2;
    color: var(--sheet-slate-500);
    position: absolute;
    top: -1rem;
    left: 50%;
    transform: translateX(-50%);
    white-space: nowrap;
  }

  .sidesDiagramWrap {
    position: relative;
    background-color: var(--sheet-slate-900-50);
    border-radius: 0.25rem;
    padding: 1.25rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border-width: 1px;
    border-style: solid;
    border-color: var(--sheet-slate-600-30);
  }

  .sidesDiagramInner {
    position: relative;
  }

  .sidesAnchorBlock {
    width: 5rem;
    height: 1.5rem;
    background-color: var(--sheet-cyan-500);
    border-radius: 0.25rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    line-height: 1.2;
    color: var(--sheet-slate-900);
    font-weight: 600;
  }

  .sideTop {
    position: absolute;
    top: -1rem;
    left: 50%;
    transform: translateX(-50%);
    font-size: 10px;
    line-height: 1.2;
    color: var(--sheet-emerald-400);
    font-weight: 500;
  }

  .sideBottom {
    position: absolute;
    bottom: -1rem;
    left: 50%;
    transform: translateX(-50%);
    font-size: 10px;
    line-height: 1.2;
    color: var(--sheet-emerald-400);
    font-weight: 500;
  }

  .sideLeft {
    position: absolute;
    top: 50%;
    left: -1.75rem;
    transform: translateY(-50%);
    font-size: 10px;
    line-height: 1.2;
    color: var(--sheet-emerald-400);
    font-weight: 500;
  }

  .sideRight {
    position: absolute;
    top: 50%;
    right: -2rem;
    transform: translateY(-50%);
    font-size: 10px;
    line-height: 1.2;
    color: var(--sheet-emerald-400);
    font-weight: 500;
  }
`;

export function AnchorCheatsheet({ withBleed = false }) {
  return (
    <CheatsheetCard title="Anchor Positioning" icon="anchor" withBleed={withBleed}>
      <div className={styles.twoColumnGrid}>
        <div>
          <Section title="Defining an Anchor">
            <PropertyRow property="anchor-name" values={['--my-anchor']} description="Creates a named anchor point" />
          </Section>

          <Section title="Positioning and Sizing">
            <PropertyRow
              property="position-anchor"
              values={['--anchor-name']}
              description="Sets default anchor for element"
            />
            <PropertyRow
              property="position-area"
              values={['top', 'bottom center', 'span-all']}
              description="Placement in the 3x3 containing block"
            />
            <PropertyRow
              property="justify-self"
              values={['anchor-center']}
              description="Inline-axis center on the anchor"
            />
            <PropertyRow
              property="Also on"
              values={['align-self', 'justify-items', 'align-items', 'place-self', 'place-items']}
            />
          </Section>

          <Section title="Fallback Positioning">
            <PropertyRow property="position-try" values={['<fallbacks> <order>']} />
            <PropertyRow property="position-try-fallbacks" values={['flip-block', 'flip-inline', 'flip-start']} />
            <PropertyRow
              property="position-try-order"
              values={['normal', 'most-height', 'most-width']}
              defaultValue="normal"
            />

            <PropertyRow
              property="position-visibility"
              values={['always', 'anchors-visible', 'no-overflow']}
              description="Hide or keep anchored element when anchor is out of view"
            />
          </Section>

          <Section title="Function Values">
            <PropertyRow
              property="Position"
              values={['anchor(top)', 'anchor(--name top)']}
              description="inset/top/left/right/bottom"
            />
            <PropertyRow
              property="Size"
              values={['anchor-size(width)', 'anchor-size(block)']}
              description="width/height/inline-size/block-size"
            />
          </Section>

          <Section title="Scope">
            <PropertyRow
              property="anchor-scope"
              values={['none', 'all', '--name']}
              defaultValue="none"
              description="Limits anchor visibility to subtree"
            />
          </Section>
        </div>

        <div>
          <Section title="Position Area Example">
            <CodeBlock language="css">{`.trigger {
  anchor-name: --btn;
}
.tooltip {
  position: absolute;
  position-anchor: --btn;
  position-area: top;
  justify-self: anchor-center;
}`}</CodeBlock>
          </Section>
          <Section title="Position Area Grid">
            <div className={styles.positionAreaGrid}>
              <div className={styles.positionAreaCellMuted}>left top</div>
              <div className={styles.positionAreaCellHighlight}>center top</div>
              <div className={styles.positionAreaCellMuted}>right top</div>
              <div className={styles.positionAreaCellHighlight}>left center</div>
              <div className={styles.positionAreaAnchorCell}>center/anchor</div>
              <div className={styles.positionAreaCellHighlight}>right center</div>
              <div className={styles.positionAreaCellMuted}>left bottom</div>
              <div className={styles.positionAreaCellHighlight}>center bottom</div>
              <div className={styles.positionAreaCellMuted}>right bottom</div>
            </div>
          </Section>

          <Section title="Fallback">
            <div className={styles.fallbackFlowRow} style={{ justifyContent: 'space-between' }}>
              <div className={styles.fallbackCase}>
                <div className={styles.fallbackButton}>btn</div>
                <div className={styles.fallbackTipBottom}>tip</div>
                <div className={styles.fallbackLabelTop}>default</div>
              </div>
              <div className={styles.fallbackArrowWrap} style={{ marginTop: '-1rem' }}>
                <ArrowRight className={styles.fallbackArrow} strokeWidth={2.25} />
              </div>
              <div className={styles.fallbackCase}>
                <div className={styles.fallbackButton}>btn</div>
                <div className={styles.fallbackTipTop}>tip</div>
                <div className={styles.fallbackLabelBottom}>flip-block</div>
              </div>
              <div className={styles.fallbackArrowWrap} style={{ marginTop: '-1rem' }}>
                <ArrowRight className={styles.fallbackArrow} strokeWidth={2.25} />
              </div>
              <div className={styles.fallbackCase}>
                <div className={styles.fallbackButton}>btn</div>
                <div className={styles.fallbackTipInline}>tip</div>
                <div className={styles.fallbackLabelInline}>flip-inline</div>
              </div>
            </div>
          </Section>

          <Section title="Function Example">
            <CodeBlock language="css">{`.tooltip {
  position: absolute;
  position-anchor: --btn;
  bottom: anchor(top);
  width: anchor-size(width);
}`}</CodeBlock>
          </Section>

          <Section title="anchor() Sides">
            <div className={styles.sidesDiagramWrap}>
              <div className={styles.sidesDiagramInner}>
                <div className={styles.sidesAnchorBlock}>center/anchor</div>
                <div className={styles.sideTop}>top</div>
                <div className={styles.sideBottom}>bottom</div>
                <div className={styles.sideLeft}>left</div>
                <div className={styles.sideRight}>right</div>
              </div>
            </div>
          </Section>
        </div>
      </div>
    </CheatsheetCard>
  );
}
